import { AxiosResponse } from "axios";
import * as React from "react";
import { Location } from "react-router-dom";
import { CloudDomainService, IDomainDto, IVerifyDomainResponse } from "../../../../services/cloud-domain-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { resolveLoadingPresentation } from "../../../../shared/utility/first-load-skeletons";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCopyButton from "../../../ui-kit/kuika-copy-button/kuika-copy-button";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import { KuikaListTableStatusCell } from "../../../ui-kit/kuika-list-table/kuika-list-table";
import { KuikaSkeletonDetail, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaH6 from "../../../ui-kit/typography/H6/H6";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import "./domain-details.scss";
import { getDomainStatusPresentation, IDomainStatusPresentation } from "./domain-status";

interface DomainDetailsProps extends IWithHistoryProp {
  location?: Location;
}

interface DomainDetailsState {
  loading: boolean;
  verifying: boolean;
  domain?: IDomainDto;
  error?: string;
  successMessage?: string;
}

interface DnsRecordRow {
  type: string;
  name: string;
  value: string;
}

class DomainDetailsComponent extends React.Component<DomainDetailsProps, DomainDetailsState> {
  constructor(props: DomainDetailsProps) {
    super(props);
    this.state = {
      loading: false,
      verifying: false,
      domain: undefined,
      error: undefined,
      successMessage: undefined
    };
  }

  componentDidMount() {
    this.fetchDomain();
  }

  componentDidUpdate(prevProps: DomainDetailsProps) {
    const previousPath = prevProps.location?.pathname ?? prevProps.navigator.location?.pathname;
    const currentPath = this.props.location?.pathname ?? this.props.navigator.location?.pathname;

    if (previousPath !== currentPath) {
      this.fetchDomain();
    }
  }

  getDomainId = (): string | undefined => {
    const path = this.props.location?.pathname ?? this.props.navigator.location?.pathname ?? "";
    if (!path) return undefined;

    const segments = path.split("/").filter(Boolean);
    if (segments.length === 0) return undefined;

    const lastSegment: string = segments[segments.length - 1];
    try {
      return decodeURIComponent(lastSegment);
    } catch (error) {
      console.error("Failed to decode domain id:", error);
      return lastSegment;
    }
  };

  fetchDomain = async (resetMessages: boolean = true): Promise<void> => {
    const domainId = this.getDomainId();
    if (!domainId) {
      this.setState({ error: "Domain identifier is missing." });
      return;
    }

    this.setState(({ successMessage }) => ({
      loading: true,
      error: undefined,
      successMessage: resetMessages ? undefined : successMessage
    }));

    try {
      const response: AxiosResponse<IDomainDto> = await CloudDomainService.GetDomainById(domainId);
      if (response && response.data) {
        this.setState({ domain: response.data, loading: false });
      } else {
        throw new Error("Domain details could not be loaded.");
      }
    } catch (error: unknown) {
      this.setState({
        loading: false,
        error: error instanceof Error ? error.message : "Failed to load domain details"
      });
    }
  };

  handleBack = () => {
    const { navigator } = this.props;
    const currentDomain = navigator.getCurrentDomain();
    if (currentDomain) {
      navigator.gotoUrl(`/${currentDomain}/cloud/domains`);
    } else {
      navigator.goBack();
    }
  };

  handleRefresh = () => {
    this.fetchDomain();
  };

  handleVerify = async () => {
    const { domain } = this.state;
    if (!domain) return;

    this.setState({ verifying: true, error: undefined, successMessage: undefined });

    try {
      const response: AxiosResponse<IVerifyDomainResponse> = await CloudDomainService.VerifyDomain(domain.id);

      // Refresh domain status after verification attempt
      await this.fetchDomain(false);

      if (response?.data?.success) {
        // Certificate is ACTIVE and map entries created
        this.setState({
          successMessage: "Domain verification complete! Your domain is now active."
        });
      } else {
        // Certificate is still PROVISIONING or was just created
        const message =
          response?.data?.events?.[0] ||
          "Certificate provisioning initiated. Please use REFRESH STATUS to check progress.";
        this.setState({
          successMessage: message
        });
      }
    } catch (error: unknown) {
      this.setState({
        error: error instanceof Error ? error.message : "Failed to verify domain"
      });
    } finally {
      this.setState({ verifying: false });
    }
  };

  render() {
    const { loading, verifying, domain, error, successMessage } = this.state;
    const domainId = this.getDomainId() ?? "";
    const displayName = domain?.domain ?? domainId;
    const status: IDomainStatusPresentation = getDomainStatusPresentation(domain?.status);

    const statusLower = (domain?.status || "").toLowerCase();
    const showPendingInstructions = statusLower === "pending" || statusLower === "provisioning";
    const isProvisioning = statusLower === "provisioning";

    // Remove trailing dots from DNS values
    const cleanDnsValue = (value: string): string => {
      if (!value) return value;
      return value.endsWith(".") ? value.slice(0, -1) : value;
    };

    // Strip base domain from DNS name to show only subdomain
    const cleanDnsName = (fullName: string, baseDomain: string): string => {
      if (!fullName || !baseDomain) return fullName;

      // Remove trailing dot first
      let cleaned = cleanDnsValue(fullName);

      // Remove base domain suffix if present
      const domainSuffix = `.${baseDomain}`;
      if (cleaned.endsWith(domainSuffix)) {
        cleaned = cleaned.slice(0, -domainSuffix.length);
      }

      return cleaned;
    };

    const dnsRecords: DnsRecordRow[] =
      domain?.dnsCnameHost && domain?.dnsCnameTarget
        ? [
            {
              type: "CNAME",
              name: cleanDnsName(domain.dnsCnameHost, domain.baseDomain || domain.domain),
              value: cleanDnsValue(domain.dnsCnameTarget)
            }
          ]
        : [];

    const columns: KuikaTableColumn<DnsRecordRow>[] = [
      {
        id: "type",
        accessorKey: "type",
        enableSorting: false,
        header: () => "Type",
        cell: (row) => <KuikaText>{row.type}</KuikaText>,
        width: 88
      },
      {
        id: "name",
        accessorKey: "name",
        enableSorting: false,
        header: () => "Name",
        cell: (row) => (
          <div className="kuika-domain-details__value-cell">
            <KuikaText className="kuika-domain-details__value-text" title={row.name}>
              {row.name}
            </KuikaText>
            <KuikaCopyButton value={row.name} type="tertiary" color="neutral" size="sm" label="Copy DNS name" />
          </div>
        )
      },
      {
        id: "value",
        accessorKey: "value",
        enableSorting: false,
        header: () => "Value",
        cell: (row) => (
          <div className="kuika-domain-details__value-cell">
            <KuikaText className="kuika-domain-details__value-text" title={row.value}>
              {row.value}
            </KuikaText>
            <KuikaCopyButton value={row.value} type="tertiary" color="neutral" size="sm" label="Copy DNS value" />
          </div>
        )
      }
    ];

    return (
      <div className="kuika-settings-panel">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="world-www" />
            <div className="kuika-domain-details__title-group">
              <KuikaH4>Domains / {displayName}</KuikaH4>
              {/* Verify and refresh move the status while the page is open, so the badge morphs to it. */}
              {domain && <KuikaListTableStatusCell label={status.label} color={status.color} animateChanges />}
            </div>
          </div>
        </div>

        <div className="kuika-domain-details__card">
          {/* The page's shape until the domain first arrives; a refresh keeps the details on screen
              and says it on its own button (L7). */}
          {resolveLoadingPresentation(loading, domain !== undefined) === "skeleton" ? (
            <KuikaSkeletonRegion label="Loading domain">
              <KuikaSkeletonDetail />
            </KuikaSkeletonRegion>
          ) : (
            <div className="kuika-domain-details__body">
              {error && <KuikaInfoBox color="red" leftUnit={<KuikaText>{error}</KuikaText>} />}
              {successMessage && !error && (
                <KuikaInfoBox color="blue" leftUnit={<KuikaText>{successMessage}</KuikaText>} />
              )}

              {!error && domain && (
                <>
                  <div className="kuika-domain-details__summary">
                    <KuikaSvg name="world-www" width={36} height={36} />
                    <div className="kuika-domain-details__summary-text">
                      <KuikaH5>
                        {statusLower === "active" && "Domain Active"}
                        {statusLower === "provisioning" && "Certificate Provisioning"}
                        {statusLower === "pending" && "Verify Your Domain"}
                        {!["active", "provisioning", "pending"].includes(statusLower) && "Domain Status"}
                      </KuikaH5>
                      <KuikaText className="kuika-domain-details__secondary-text">
                        {statusLower === "active" && "Your domain is active and ready to use."}
                        {statusLower === "provisioning" &&
                          "Your domain is being provisioned. This requires the DNS CNAME record below to be correctly configured at your domain provider."}
                        {statusLower === "pending" &&
                          "Add the DNS record below at your domain provider. Once the record is live, click START VERIFICATION."}
                        {!["active", "provisioning", "pending"].includes(statusLower) &&
                          `The domain is currently ${status.label.toLowerCase()}.`}
                      </KuikaText>
                    </div>
                  </div>

                  {dnsRecords.length > 0 ? (
                    <div className="kuika-domain-details__records">
                      <KuikaH6>DNS Configuration</KuikaH6>
                      <KuikaTable<DnsRecordRow>
                        data={dnsRecords}
                        columns={columns}
                        rowKey={(row) => `${row.type}-${row.name}`}
                        enablePagination={false}
                        fitContentHeight
                      />
                    </div>
                  ) : (
                    <KuikaText className="kuika-domain-details__secondary-text">
                      DNS record information is not yet available. Please try refreshing in a few minutes.
                    </KuikaText>
                  )}

                  {isProvisioning && dnsRecords.length > 0 && (
                    <KuikaInfoBox
                      color="blue"
                      leftUnit={
                        <KuikaText>
                          <strong>Certificate provisioning in progress.</strong> This can take 10-60 minutes. Ensure the
                          CNAME record above is correctly added to your domain provider's DNS settings. Click{" "}
                          <strong>REFRESH STATUS</strong> below to check if provisioning is complete.
                        </KuikaText>
                      }
                    />
                  )}

                  <div className="kuika-domain-details__actions">
                    <KuikaButton type="tertiary" color="neutral" size="md" onClick={this.handleBack}>
                      GO BACK
                    </KuikaButton>
                    {showPendingInstructions && isProvisioning && (
                      <KuikaButton type="primary" size="md" onClick={this.handleRefresh} loading={loading}>
                        REFRESH STATUS
                      </KuikaButton>
                    )}
                    {showPendingInstructions && !isProvisioning && (
                      <KuikaButton
                        type="primary"
                        size="md"
                        onClick={this.handleVerify}
                        loading={verifying}
                        disabled={dnsRecords.length === 0}
                      >
                        START VERIFICATION
                      </KuikaButton>
                    )}
                  </div>
                </>
              )}

              {!error && !domain && (
                <KuikaText className="kuika-domain-details__secondary-text">
                  Domain details are not available.
                </KuikaText>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const DomainDetails = withManagement(withHistory(DomainDetailsComponent));
export { DomainDetails };
